import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

const frPromotionCopy = {
    'One platform for all your financial needs.':
        'Une seule plateforme pour tous vos besoins financiers.',
    'Send money, pay bills, top up wallets, and manage payments in one app.':
        'Envoyez de l argent, payez vos factures, rechargez vos wallets et gerez vos paiements dans une seule app.',
    'Top up fast or move value directly into another MojoPayr wallet.':
        'Ajoutez des fonds ou envoyez directement vers un autre wallet MojoPayr.',
    'Transfer money home instantly.':
        'Envoyez de l argent au pays instantanement.',
    'Pay taxes online using MojoPayr.':
        'Payez vos taxes en ligne avec MojoPayr.',
    'Diaspora transfers now faster.':
        'Les transferts diaspora sont maintenant plus rapides.',
    'Send money to Sierra Leone with lower fees this weekend.':
        'Envoyez de l argent en Sierra Leone avec des frais reduits ce week-end.',
    'New QR merchant payments now available.':
        'Les paiements marchands par QR sont maintenant disponibles.',
    'Pay electricity bills directly with MojoPayr.':
        'Payez vos factures d electricite directement avec MojoPayr.',
    'Tickets for Afro Fest now available.':
        'Les billets pour Afro Fest sont maintenant disponibles.',
    'Instant wallet funding now active.':
        'La recharge instantanee du wallet est maintenant active.',
    'Learn more': 'En savoir plus',
    Open: 'Ouvrir',
    'Get started': 'Commencer',
    'Pay now': 'Payer maintenant',
}

const localizePromotionText = (value, lang) => {
    const text = String(value || '').trim()
    return lang === 'fr' ? (frPromotionCopy[text] || text) : text
}

const normalizePromotions = (payload, lang) => {
    const items = Array.isArray(payload) ? payload : payload?.data
    if (!Array.isArray(items)) return []
    return items
        .map((item) => ({
            id: item.id,
            title: localizePromotionText(item.title_fr || item.title, lang),
            message: localizePromotionText(item.message_fr || item.message, lang),
            ctaText: localizePromotionText(item.cta_text_fr || item.cta_text, lang),
            ctaRoute: String(item.cta_route || '').trim(),
            imageUrl: String(item.image_url || item.image || '').trim(),
            backgroundColor: String(item.background_color || '').trim(),
        }))
        .filter((item) => item.title || item.message)
}

const normalizeCountry = (value) => {
    const text = String(value || '').trim()
    const key = text.toLowerCase()
    const mapped = {
        'united states': 'US',
        canada: 'CA',
        'united kingdom': 'GB',
        uk: 'GB',
        australia: 'AU',
        france: 'FR',
        'sierra leone': 'SL',
        liberia: 'LR',
        guinea: 'GN',
        gambia: 'GM',
        cameroon: 'CM',
    }[key]
    return mapped || text.toUpperCase()
}

const PromotionCarousel = ({
    platform = 'website',
    country,
    currency,
    userType,
    fallbackTitle = 'MojoPayr',
    fallbackMessage = 'Top up fast or move value directly into another MojoPayr wallet.',
    compact = false,
    sx = {},
}) => {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const [promotions, setPromotions] = useState([])
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        let alive = true
        api.get('/promotions/active', {
            params: {
                platform,
                lang,
                ...(country ? { country: normalizeCountry(country) } : {}),
                ...(currency ? { currency } : {}),
                ...(userType ? { user_type: userType } : {}),
            },
        })
            .then((response) => {
                if (!alive) return
                setPromotions(normalizePromotions(response.data, lang))
                setActiveIndex(0)
            })
            .catch(() => {
                if (!alive) return
                setPromotions([])
            })

        return () => {
            alive = false
        }
    }, [platform, country, currency, userType, lang])

    useEffect(() => {
        if (promotions.length < 2) return undefined
        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % promotions.length)
        }, 6000)
        return () => window.clearInterval(timer)
    }, [promotions.length])

    const activePromotion = useMemo(() => {
        if (promotions.length === 0) {
            return {
                title: fallbackTitle,
                message: fallbackMessage,
                ctaText: '',
                ctaRoute: '',
                imageUrl: '',
                backgroundColor: '',
            }
        }
        return promotions[Math.min(activeIndex, promotions.length - 1)]
    }, [activeIndex, fallbackMessage, fallbackTitle, promotions])

    const openCta = () => {
        const route = activePromotion.ctaRoute
        if (!route) return
        if (route.startsWith('https://')) {
            window.location.href = route
            return
        }
        if (route.startsWith('/')) {
            navigate(route)
        }
    }

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: compact ? 3 : 4,
                border: '1px solid rgba(255,255,255,0.12)',
                background: activePromotion.backgroundColor
                    ? `linear-gradient(135deg, ${activePromotion.backgroundColor}, rgba(91,46,255,0.34))`
                    : 'linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,138,0,0.10))',
                p: compact ? 1.6 : 2.2,
                ...sx,
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                {activePromotion.imageUrl ? (
                    <Box
                        component="img"
                        src={activePromotion.imageUrl}
                        alt=""
                        loading="lazy"
                        sx={{
                            width: compact ? 34 : 42,
                            height: compact ? 34 : 42,
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                            bgcolor: 'rgba(255,255,255,0.08)',
                        }}
                    />
                ) : null}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontWeight: 900,
                            fontSize: compact ? 14 : 16,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {activePromotion.title}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.76)',
                            fontSize: compact ? 12.5 : 14,
                            lineHeight: 1.55,
                            display: '-webkit-box',
                            WebkitLineClamp: compact ? 1 : 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {activePromotion.message}
                    </Typography>
                </Box>

                {activePromotion.ctaText && activePromotion.ctaRoute ? (
                    <Button
                        onClick={openCta}
                        sx={{
                            flexShrink: 0,
                            bgcolor: 'rgba(255,255,255,0.14)',
                            color: '#FFFFFF',
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 900,
                            px: compact ? 1.5 : 2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
                        }}
                    >
                        {activePromotion.ctaText}
                    </Button>
                ) : null}
            </Stack>

            {promotions.length > 1 ? (
                <Stack direction="row" spacing={0.6} sx={{ mt: 1.3 }}>
                    {promotions.map((promotion, index) => (
                        <Box
                            key={promotion.id || index}
                            onClick={() => setActiveIndex(index)}
                            sx={{
                                width: index === activeIndex ? 16 : 6,
                                height: 6,
                                borderRadius: 999,
                                cursor: 'pointer',
                                bgcolor: index === activeIndex ? '#FFFFFF' : 'rgba(255,255,255,0.36)',
                                transition: 'all .18s ease',
                            }}
                        />
                    ))}
                </Stack>
            ) : null}
        </Box>
    )
}

export default PromotionCarousel
